/**
 * Modal de detalhe de execução — compartilhado entre TabExecucoes e Dashboard.
 */

import { useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import type { CurrentUser } from "../../lib/auth";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ConversationModal } from "./ConversationModal";
import { ToolCall, ToolCallPopup } from "./ToolCallPopup";
import { DialogPanel } from "./DialogPanel";
import { AlertTriangleIcon, MicIcon, WhatsAppIcon, InstagramIcon, MessengerIcon } from "./Icons";
import { backdropClose } from "./backdrop";

const _authedRoute = getRouteApi("/_authed");
const ERR_STYLE = { fontSize: 12, color: "var(--danger)", whiteSpace: "pre-wrap", wordBreak: "break-word" } as const;
const GENERIC_CAUSE = "Indisponibilidade temporária do sistema. A equipe técnica já foi avisada.";

// Erro de um passo: causa traduzida (catálogo error_causes.py, gravada no data
// pelo tracker) com o erro cru recolhido. Causa de visibilidade admin vira o
// texto genérico pra quem não é Admin. Sem causa: erro cru, como sempre.
function CauseBox({ d }: { d: Record<string, any> }) {
  const ctx = _authedRoute.useRouteContext() as { user?: CurrentUser };
  const isAdmin = ctx.user?.role_name === "Admin";
  const raw = d.error != null ? String(d.error) : null;
  if (!d.cause_text) return raw ? <div style={ERR_STYLE}>{raw}</div> : null;
  const hidden = d.cause_visibility === "admin" && !isAdmin;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={ERR_STYLE}>{hidden ? GENERIC_CAUSE : String(d.cause_text)}</div>
      {!hidden && d.cause_action && (
        <div style={{ fontSize: 12, color: "var(--ink-mid)" }}>O que fazer: {String(d.cause_action)}</div>
      )}
      {isAdmin && raw && (
        <details>
          <summary style={{ fontSize: 11, color: "var(--ink-muted)", cursor: "pointer" }}>Detalhes técnicos</summary>
          <div style={ERR_STYLE}>{raw}</div>
        </details>
      )}
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────

export function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

export function fmtDuration(ms: number | null) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ── ordem real do fio nos payloads de webhook ─────────────────────────────────
// O payload é gravado em coluna JSONB e o Postgres reordena as chaves por
// tamanho (lead, meta, event, fromMe…). Na exibição restauramos a ordem de
// construção do envelope v2 (envelope.py) / value da Meta. Chave desconhecida
// mantém a posição relativa, depois das conhecidas.
const WIRE_ORDERS: Record<string, string[]> = {
  "": ["event", "schema_version", "channel", "source", "fromMe", "timestamp", "lead", "message", "meta"],
  lead: ["bsuid", "username", "phone", "profile_name", "igsid", "psid"],
  message: ["id", "type", "text", "media", "quote", "ads", "contacts", "location", "reaction", "interactive", "flow", "template"],
  template: ["name", "language", "components"],
  media: ["url", "caption", "mime", "filename"],
  quote: ["id", "text"],
  ads: ["ctwa_clid", "referral"],
  reaction: ["emoji", "message"],
  interactive: ["id", "title", "type", "header", "body", "action", "footer", "buttons", "url", "sections", "cards", "flow"],
  cards: ["index", "image", "text", "buttons"],
  buttons: ["id", "title"],
  header: ["type", "text", "url"],
  url: ["display_text", "url"],
  // path-qualificado: message.flow (inbound) tem campos arbitrários do Flow
  // do cliente e NÃO pode ser reordenado — só o interactive.flow do outbound.
  "interactive.flow": ["id", "name", "cta", "screen"],
  meta: ["messaging_product", "recipient_type", "metadata", "contacts", "messages", "message_echoes", "statuses", "to", "type"],
  metadata: ["display_phone_number", "phone_number_id"],
  contacts: ["profile", "wa_id", "user_id"],
  messages: ["context", "from", "from_user_id", "id", "timestamp", "text", "type"],
  message_echoes: ["context", "from", "to", "id", "timestamp", "text", "type"],
  context: ["from", "id"],
};

function orderWire(v: unknown, key = "", parent = ""): unknown {
  if (Array.isArray(v)) return v.map(x => orderWire(x, key, parent));
  if (v === null || typeof v !== "object") return v;
  const obj = v as Record<string, unknown>;
  const order = WIRE_ORDERS[`${parent}.${key}`] ?? WIRE_ORDERS[key];
  const keys = Object.keys(obj);
  if (order) {
    keys.sort((a, b) =>
      (order.includes(a) ? order.indexOf(a) : order.length) -
      (order.includes(b) ? order.indexOf(b) : order.length));
  }
  const out: Record<string, unknown> = {};
  for (const k of keys) out[k] = orderWire(obj[k], k, key);
  return out;
}

// ── status meta ───────────────────────────────────────────────────────────────

export const EXEC_META: Record<string, { color: string; bg: string; label: string }> = {
  running:            { color: "var(--info)", bg: "var(--info-soft)",   label: "Rodando" },
  completed:          { color: "var(--ok)", bg: "var(--ok-soft)",    label: "Sucesso" },
  completed_with_errors: { color: "var(--warn)", bg: "var(--warn-soft)", label: "Sucesso parcial" },
  completed_with_fallback: { color: "var(--ok)", bg: "var(--ok-soft)", label: "Fallback" },
  window_closed:      { color: "var(--orange)", bg: "var(--warn-soft)", label: "Janela fechada" },
  deferred:           { color: "var(--ink-soft)",    bg: "var(--surface-app)",  label: "Adiado" },
  lock_busy:          { color: "var(--ink-soft)",    bg: "var(--surface-app)",  label: "Lock busy" },
  waiting_agent:      { color: "var(--warn)", bg: "var(--warn-soft)",   label: "Aguardando agente" },
  blocked:            { color: "var(--orange)", bg: "var(--pending-soft)",   label: "Bloqueado" },
  human_block_set:    { color: "var(--orange)", bg: "var(--pending-soft)",   label: "Humano" },
  human_blocked:      { color: "var(--orange)", bg: "var(--pending-soft)",   label: "Humano" },
  crm_ia_on:          { color: "var(--pink)", bg: "var(--pink-soft)",   label: "CRM Ligar IA" },
  crm_ia_off:         { color: "var(--pink)", bg: "var(--pink-soft)",   label: "CRM Desligar IA" },
  crm_ia_on_noop:     { color: "var(--pink)", bg: "var(--pink-soft)",   label: "IA já estava ligada" },
  crm_ia_off_noop:    { color: "var(--pink)", bg: "var(--pink-soft)",   label: "IA já estava desligada" },
  stale:              { color: "var(--orange)", bg: "var(--warn-soft)", label: "Atrasada" },
  ai_paused:          { color: "var(--orange)", bg: "var(--warn-soft)", label: "IA Pausada" },
  reset_applied:      { color: "var(--danger)", bg: "var(--violet-soft)",  label: "Reset" },
  whitelist_registered: { color: "var(--ink-secondary)", bg: "var(--surface-app)", label: "Cadastro na whitelist" },
  invalid_payload:    { color: "var(--danger)", bg: "var(--danger-soft)",    label: "Payload inválido" },
  blocked_whitelist:  { color: "var(--ink-soft)",    bg: "var(--surface-app)",  label: "White bloqueado" },
  blocked_blacklist:  { color: "var(--ink-soft)",    bg: "var(--surface-app)",  label: "Black bloqueado" },
  blocked_max_messages:{ color: "var(--orange)", bg: "var(--pending-soft)",   label: "Limite atingido" },
  llm_failed:         { color: "var(--danger)", bg: "var(--danger-soft)",    label: "Erro LLM" },
  send_failed:        { color: "var(--danger)", bg: "var(--danger-soft)",    label: "Erro envio" },
  aborted_post_reset: { color: "var(--ink-soft)",    bg: "var(--surface-app)",  label: "Abortado" },
  error:              { color: "var(--danger)", bg: "var(--danger-soft)",    label: "Erro" },
  failed:             { color: "var(--danger)", bg: "var(--danger-soft)",    label: "Falhou" },
  sent:               { color: "var(--ok)", bg: "var(--ok-soft)",    label: "Enviado" },
  finalized:          { color: "var(--ink-soft)",    bg: "var(--surface-app)",  label: "Finalizado" },
  transferred:        { color: "var(--orange)", bg: "var(--pending-soft)",   label: "Transferido" },
  already_done:       { color: "var(--ink-soft)",    bg: "var(--surface-app)",  label: "Já concluído" },
  cancelled:          { color: "var(--ink-soft)",    bg: "var(--surface-app)",  label: "Cancelada" },
  no_clients:         { color: "var(--ink-soft)",    bg: "var(--surface-app)",  label: "Sem clientes" },
  no_channel:         { color: "var(--ink-soft)",    bg: "var(--surface-app)",  label: "Sem canal" },
  connection_down:    { color: "var(--orange)", bg: "var(--pending-soft)",   label: "WABA desconectada" },
};

export function execMeta(status: string) {
  return EXEC_META[status] ?? { color: "var(--ink-soft)", bg: "var(--surface-app)", label: status };
}

export const TYPE_LABEL: Record<string, string> = {
  process_message: "Conversa",
  followup:        "Follow-up",
  webhook:         "Webhook",
  patioo:          "Patioo",
  call_summary:    "Ligação",
  call_event:      "Evento de Ligação",
  portal:          "Portal (e-mail)",
};

export function typeLabel(t: string) {
  return TYPE_LABEL[t] ?? t;
}

// ── canal de origem ───────────────────────────────────────────────────────────
// Canais futuros: basta adicionar uma entrada aqui (ícone + cor da marca + label).
export const CHANNEL_META: Record<string, {
  label: string; color: string;
  Icon: (p: { size?: number; style?: React.CSSProperties }) => React.ReactElement;
}> = {
  waba:      { label: "WhatsApp",  color: "#25D366", Icon: WhatsAppIcon },
  instagram: { label: "Instagram", color: "#E1306C", Icon: InstagramIcon },
  messenger: { label: "Messenger", color: "#0084FF", Icon: MessengerIcon },
  // Conexão externa da WABA (sem Meta): é WhatsApp por fora, só a rota muda.
  externo:   { label: "WhatsApp (conexão externa)", color: "#25D366", Icon: WhatsAppIcon },
};

export function ChannelBadge({ channel }: { channel?: string | null }) {
  const cm = channel ? CHANNEL_META[channel] : null;
  if (!cm) return <span style={{ color: "var(--ink-dim)" }}>—</span>;
  return (
    <span title={cm.label} style={{ display: "inline-flex", alignItems: "center", gap: 4, color: cm.color }}>
      <cm.Icon size={16} />
      {channel === "externo" && (
        <span style={{ fontSize: 9, fontWeight: 600, lineHeight: 1, padding: "2px 5px", borderRadius: 4, background: "var(--surface-input)", border: "1px solid var(--line-strong)", color: "var(--ink-secondary)" }}>
          Externa
        </span>
      )}
    </span>
  );
}

// Steps que não precisam de cascata (dados internos irrelevantes para o usuário)
const NO_EXPAND_STEPS = new Set([
  "access_check", "debounce_check", "lock_acquire", "human_detection",
  "block_check", "sanity_check", "quote_extraction", "persist_response",
  "load_conversation", "load_history", "load_followup", "media_detection",
]);

// ── step labels ───────────────────────────────────────────────────────────────

const STEP_LABELS: Record<string, string> = {
  access_check:     "Checagem de Acesso",
  agent_inactive:   "Agente Desativado",
  stale_message:    "Mensagem Atrasada",
  ai_pause_set:     "Pausa da IA",
  debounce_check:   "Espera de Mensagens",
  lock_acquire:     "Controle de Concorrência",
  pre_transcription:"Transcrição de Mídia",
  call_summary:     "Resumo da Ligação",
  call_event:       "Registro de Ligação",
  load_conversation:"__hidden__",
  reset_check:      "__hidden__",
  sanity_check:     "__hidden__",
  render_prompt:    "Prompt renderizado",
  persist_response: "__hidden__",
  human_detection:  "Detecção Humana",
  block_check:      "Bloqueio por Atendimento Humano",
  interaction_limit_check: "Limite de Interações",
  media_detection:  "Tipo de Mensagem",
  media_consensus:  "Consenso de Mídias",
  ad_detection:     "Detecção de Ads",
  quote_extraction: "Extração de Citação",
  final_message:    "Mensagem Final",
  load_history:     "Histórico de Mensagens",
  load_tools:       "__hidden__",
  prompt_cache:     "Cache de Prompt",
  llm_tool_loop:    "Agente de IA",
  subagent_llm:     "Tool (IA interna)",
  estoque_tool:     "Tool de Estoque",
  web_tool:         "Busca na internet",
  // followup steps
  load_channel:     "__hidden__",
  load_subagent:    "__hidden__",
  load_followup:    "Contagem de Follow-ups",
  llm:              "Agente de IA",
  persist:          "__hidden__",
  transcription:    "Transcrição de Mídia",
  voice_synthesis:  "Síntese de Voz",
  voice:            "Voz (IA)",
  tts:              "Síntese de Voz (TTS)",
  crmillion:        "CRMillion",
  send_retry:       "Envio falhou, tentando de novo",
  send_message:     "Envio da Resposta",
  delivery_failed:  "Entrega Recusada (Meta)",
  cancelled:        "Cancelado",
};

// Custo do step com desconto de cache: a parcela cacheada do input usa
// price_cached_usd (ou o preço de input normal quando não cadastrado).
function stepCost(data: Record<string, any>): number {
  const tin = Number(data.tokens_in ?? 0);
  const tout = Number(data.tokens_out ?? 0);
  // Clamp espelha o _cost_usd do backend: cache nunca excede o input.
  const tcache = Math.min(Number(data.tokens_cached ?? 0), tin);
  const pin = Number(data.price_in_usd ?? 0);
  const pout = Number(data.price_out_usd ?? 0);
  const pcache = Number(data.price_cached_usd ?? 0) || pin;
  // Escrita no cache (Anthropic) já está em tokens_in ao preço de input; soma só a
  // diferença até o preço de escrita gravado no step (sem preço = sem prêmio).
  const twrite = Number(data.tokens_cache_write ?? 0);
  const pwrite = Number(data.price_cache_write_usd ?? 0) || pin;
  return ((tin - tcache) * pin + tcache * pcache + twrite * (pwrite - pin) + tout * pout) / 1e6;
}

// Custo pequeno (Haiku/cache) não pode virar $0.0000 — abre casas abaixo de 1 centésimo de centavo.
const fmtUsd = (v: number) => v.toFixed(v < 0.0001 ? 6 : 4);

// Popup de JSON cru (carga da Meta, retorno da LLM). data: undefined = carregando,
// null = não encontrado. Fecha no Esc, no botão e clicando fora.
function JsonPopup({ title, data, emptyText, onClose, nested }: {
  title: string; data: unknown; emptyText: string; onClose: () => void; nested?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const text = data == null ? "" : JSON.stringify(data, null, 2);
  const btn: React.CSSProperties = {
    fontSize: 11, padding: "2px 8px", borderRadius: 4, cursor: "pointer",
    border: "1px solid var(--line-strong)", background: "var(--line)", color: "var(--ink-secondary)",
  };
  return (
    <div
      {...backdropClose(onClose)}
      style={{
        position: "fixed", inset: 0, background: "var(--overlay)",
        zIndex: (nested ? "calc(var(--z-modal) + 300)" : "calc(var(--z-modal) + 100)") as any, display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <DialogPanel onClose={onClose} label={title} style={{
        background: "var(--surface-hover)", border: "1px solid var(--line-strong)",
        borderRadius: 12, width: 560, maxWidth: "calc(100vw - 32px)", maxHeight: "80vh",
        display: "flex", flexDirection: "column", boxShadow: "var(--shadow-modal)",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 16px", borderBottom: "1px solid var(--line)", flexShrink: 0,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-strong)" }}>{title}</span>
          <div style={{ display: "flex", gap: 8 }}>
            {text && (
              <button style={btn} onClick={() => {
                navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}>{copied ? "✓ Copiado" : "Copiar"}</button>
            )}
            <button style={btn} onClick={onClose}>Fechar</button>
          </div>
        </div>
        <div style={{ overflow: "auto", padding: "12px 16px" }}>
          {data === undefined ? (
            <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>Carregando...</span>
          ) : data === null ? (
            <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>{emptyText}</span>
          ) : (
            <pre style={{
              margin: 0, fontSize: 11, color: "var(--ink-mid)", whiteSpace: "pre-wrap",
              wordBreak: "break-all", fontFamily: "var(--font-mono)", lineHeight: 1.6,
            }}>{text}</pre>
          )}
        </div>
      </DialogPanel>
    </div>
  );
}

// Linha de custo dos subagentes (transcrição/ads/voz) — modelo, tokens e custo
// dentro do próprio log, sem um "Tool IA interna" separado.
function SubagentMeta({ data }: { data: Record<string, any> }) {
  const model = data.model_name as string | undefined;
  const provider = data.model_provider as string | undefined;
  const tin = Number(data.tokens_in ?? 0);
  const tout = Number(data.tokens_out ?? 0);
  const tthink = Number(data.tokens_thinking ?? 0);
  const tcache = Math.min(Number(data.tokens_cached ?? 0), tin);
  const cost = stepCost(data);
  if (!model && tin === 0 && tout === 0) return null;
  return (
    <div style={{ fontSize: 11, color: "var(--ink-muted)", display: "flex", flexWrap: "wrap", gap: 8 }}>
      {model && <span>Modelo: <strong style={{ color: "var(--ink-mid)" }}>{provider ? `${provider}/` : ""}{model}</strong></span>}
      {tin > 0 && <span>Input: <strong style={{ color: "var(--ink-mid)" }}>{(tin - tcache).toLocaleString()} tk</strong></span>}
      {tcache > 0 && <span>Cache: <strong style={{ color: "var(--ink-mid)" }}>{tcache.toLocaleString()} tk</strong></span>}
      {tout > 0 && <span>Output: <strong style={{ color: "var(--ink-mid)" }}>{tout.toLocaleString()} tk</strong></span>}
      {tthink > 0 && <span>Thinking: <strong style={{ color: "var(--ink-mid)" }}>{tthink.toLocaleString()} tk</strong></span>}
      {cost > 0 && <span>Custo: <strong style={{ color: "var(--ink-mid)" }}>${fmtUsd(cost)}</strong></span>}
    </div>
  );
}

// ── step summary (legível) ────────────────────────────────────────────────────

/** Linha do fallback de modelo: no step que falhou e no que assumiu. */
function fallbackSummary(data: Record<string, unknown>): string {
  if (data.fallback_to) return `Modelo falhou · caiu para ${data.fallback_to}`;
  if (data.fallback_from) return `Fallback · ${data.fallback_from} falhou`;
  return "";
}

function stepSummary(type: string, data: Record<string, unknown>): string {
  const status = data.status as string;
  switch (type) {
    case "web_tool": {
      const OUTCOME: Record<string, string> = {
        hit: "cache", pinned: "cache fixo", miss: "busca nova", expired: "cache expirado → busca nova", fetched: "busca + páginas abertas",
      };
      const parts = [
        OUTCOME[String(data.cache_outcome ?? "")] ?? "busca",
        data.results != null ? `${data.results} resultados` : "",
        Number(data.pages ?? 0) > 0 ? `${data.pages} páginas` : "",
        data.elapsed_ms != null ? `${(Number(data.elapsed_ms) / 1000).toFixed(1)}s` : "",
      ];
      return parts.filter(Boolean).join(" · ");
    }
    case "estoque_tool": {
      const OUTCOME: Record<string, string> = {
        hit: "cache reaproveitado", created: "cache criado", fallback: "cache falhou (rodou integral)",
      };
      const cache = OUTCOME[String(data.cache_outcome ?? "")] ?? "sem cache";
      const rota = data.rota ? `rota ${data.rota}` : "";
      return [data.loja, rota, cache].filter(Boolean).join(" · ");
    }
    case "access_check": {
      const numero = data.numero as string | undefined;
      if (status === "blocked_whitelist")
        return `Whitelist ativada · ${numero || "Número"} não consta na lista`;
      if (status === "whitelist_registered")
        return `#cadastro · ${numero || "Número"} adicionado à whitelist`;
      if (status === "blocked_blacklist")
        return `${numero || "Número"} está na Blacklist`;
      const wl = Number(data.whitelist_size ?? 0);
      if (wl > 0) return `Whitelist ativada · ${numero || "Número"} liberado`;
      return "Número liberado";
    }
    case "debounce_check": {
      if (status === "deferred") return `Adiado · aguardou ${data.wait_seconds}s`;
      const count = Number(data.pending_count ?? 1);
      const noun = count === 1 ? "mensagem processada" : "mensagens processadas";
      return `Aguardou ${data.wait_seconds}s · ${count} ${noun}`;
    }
    case "lock_acquire": {
      const numero = data.numero as string | undefined;
      if (status === "lock_busy") return `${numero || "Conversa"} bloqueada para resposta`;
      return `${numero || "Conversa"} liberada para resposta`;
    }
    case "load_conversation":
      return data.is_new ? "Nova conversa iniciada" : "Conversa existente carregada";
    case "human_detection": {
      if (status === "human_block_set") {
        const reason = data.reason as string | undefined;
        return reason ? `Humano detectado · ${reason}` : "Humano detectado";
      }
      return "Humano não detectado";
    }
    case "crm_ia_command": {
      const on = String(data.controle_ia) === "ligar";
      if (status.endsWith("_noop")) return on ? "Comando CRM · IA já estava ligada" : "Comando CRM · IA já estava desligada";
      return on ? `Comando CRM · IA ligada${data.reseta_follow ? " · follow-up reaberto" : ""}` : "Comando CRM · IA desligada";
    }
    case "block_check":
      if (status === "ai_paused") return "IA pausada por tool — mensagem registrada como contexto";
      return status === "blocked" ? "Conversa bloqueada por atendimento humano" : "Sem bloqueio de humano ativo";
    case "ai_pause_set":
      return `IA pausada por ${data.minutes} min após esta resposta`;
    case "agent_inactive":
      return "Agente desativado — mensagem registrada como contexto";
    case "stale_message":
      return `Chegou ${Math.round(Number(data.age_s ?? 0) / 60)} min atrasada — registrada como contexto, sem resposta`;
    case "interaction_limit_check":
      return status === "blocked_max_messages"
        ? `Limite de interação atingido · cliente enviou ${data.count} de ${data.limit}`
        : `Dentro do limite · ${data.count} de ${data.limit ?? "∞"}`;
    case "sanity_check":
      return status === "invalid_payload" ? "Payload inválido — sem campo 'message'" : "Payload válido";
    case "media_detection":
      return `Tipo detectado: ${data.msg_type || "texto"}`;
    case "media_consensus":
      return status === "error"
        ? `Falha no consenso · usando ${data.media_count} transcrições individuais`
        : `${data.media_count} mídias consolidadas num relato único`;
    case "ad_detection": {
      if (!data.found) return "Nenhum ad detectado";
      const summary = data.summary as string | undefined;
      return summary
        ? `Ad detectado · ${summary.slice(0, 80)}${summary.length > 80 ? "…" : ""}`
        : "Ad detectado";
    }
    case "quote_extraction": {
      if (!data.found) return "Nenhuma citação detectada";
      const preview = data.preview as string | undefined;
      return preview ? `Citação: "${preview}"` : "Citação detectada";
    }
    case "final_message": {
      const parts: string[] = [];
      const tc = Number(data.text_count ?? 0);
      if (tc > 0) parts.push(`${tc} ${tc === 1 ? "mensagem de texto" : "mensagens de texto"}`);
      const MEDIA_LABELS: Record<string, string> = {
        audioMessage: "áudio", videoMessage: "vídeo",
        imageMessage: "imagem", stickerMessage: "figurinha", documentMessage: "documento",
        audio: "áudio", video: "vídeo", image: "imagem", sticker: "figurinha", document: "documento",
      };
      const mediaTypes = (data.media_types as string[] | undefined) ?? [];
      const mediaCounts: Record<string, number> = {};
      for (const mt of mediaTypes) {
        const lbl = MEDIA_LABELS[mt] ?? mt;
        mediaCounts[lbl] = (mediaCounts[lbl] ?? 0) + 1;
      }
      for (const [lbl, cnt] of Object.entries(mediaCounts))
        parts.push(`${cnt} ${lbl}`);
      if (data.has_ad) parts.push("1 ads");
      if (data.has_quote) parts.push("1 citação");
      return parts.length > 0 ? parts.join(" + ") : `${data.length ?? 0} caracteres`;
    }
    case "load_history": {
      const sent = Number(data.messages ?? 0);
      const total = Number(data.total ?? sent);
      if (total > sent)
        return `${sent} de ${total} enviadas como contexto · ${total - sent} fora do limite`;
      return `${sent} ${sent === 1 ? "mensagem" : "mensagens"} de contexto`;
    }
    case "load_followup": {
      const fc = Number(data.follow_count ?? 0);
      return `Follow-up nº ${fc + 1}`;
    }
    case "llm":  // followup usa "llm" em vez de "llm_tool_loop"
      return fallbackSummary(data);
    case "render_prompt":
      return status === "warn" ? "renderizado com avisos (prompt cru)" : `${data.length ?? 0} caracteres`;
    case "load_tools": {
      const c = Number(data.count ?? 0);
      return `${c} ${c === 1 ? "ferramenta configurada" : "ferramentas configuradas"}`;
    }
    case "prompt_cache":
      return (data.summary as string) || (data.used_cache ? "Cache utilizado" : "Prompt integral");
    case "llm_tool_loop":
      return fallbackSummary(data); // o resto fica visível na cascata
    case "subagent_llm": {
      // Tool python que chamou ai() internamente — mostra tool · modelo · tokens.
      const lbl = (data.agent_label as string) || "tool";
      const model = data.model_name as string | undefined;
      const tin = Number(data.tokens_in ?? 0);
      const tout = Number(data.tokens_out ?? 0);
      if (status === "error") return `${lbl} · falhou`;
      const parts = [lbl];
      if (model) parts.push(model);
      if (tin || tout) parts.push(`${tin.toLocaleString()}→${tout.toLocaleString()} tk`);
      return parts.join(" · ");
    }
    case "send_message": {
      if (status === "error") return "Falha no envio";
      return (data.mode as string) === "audio" ? "Áudio enviado" : "Texto enviado";
    }
    case "transcription":
      return `Mídia transcrita · ${data.output_len ?? 0} caracteres`;
    case "voice_synthesis":
      return status === "error" ? "Falha na síntese — fallback para texto" : `Áudio sintetizado · ${data.bytes ?? 0} bytes`;
    case "crmillion": {
      const name = (data.target_name as string) || (data.target_id as string) || "";
      switch (status) {
        case "disabled": return "Desabilitado";
        case "already_set": return "Já distribuído (1x por cliente)";
        case "distributed_to_team": return name ? `Equipe: ${name}` : "Distribuído para equipe";
        case "assigned_to_user": return name ? `Pessoa: ${name}` : "Atribuído a pessoa";
        case "kept_existing_attendant": {
          const att = data.existing_attendant as { name?: string } | undefined;
          return att?.name ? `Atendente mantido: ${att.name}` : "Atendente existente mantido";
        }
        case "no_lead_found": return "Lead não encontrado no CRM";
        case "incomplete_config": return "Configuração incompleta";
        case "get_lead_failed":
        case "distribute_failed":
        case "token_decrypt_failed":
        case "error": return `Erro: ${(data.error as string) || status}`;
        default: return `CRMillion: ${status}`;
      }
    }
    case "persist_response":
      return "Resposta salva no banco";
    case "cancelled":
      return data.at ? `Cancelado pelo usuário · ${data.at}` : "Cancelado pelo usuário";
    default:
      return "";
  }
}

// ── step visual ───────────────────────────────────────────────────────────────

function stepVis(status: string): { color: string; icon: string } {
  if (status === "ok" || status === "warn") return { color: "var(--ok)", icon: "✓" };
  if (status === "error" || status.endsWith("_failed") || status.startsWith("failed"))
    return { color: "var(--danger)", icon: "✕" };
  if (["blocked", "blocked_whitelist", "blocked_blacklist", "blocked_max_messages",
       "human_block_set", "lock_busy", "invalid_payload", "ai_paused"].includes(status))
    return { color: "var(--orange)", icon: "!" };
  if (status === "deferred") return { color: "var(--ink-soft)", icon: "—" };
  if (status === "cancelled") return { color: "var(--ink-mid)", icon: "⊘" };
  if (status === "reset_applied") return { color: "var(--danger)", icon: "↺" };
  if (status === "disabled" || status === "already_set") return { color: "var(--line-max)", icon: "—" };
  if (status === "distributed_to_team" || status === "assigned_to_user" || status === "kept_existing_attendant")
    return { color: "var(--ok)", icon: "✓" };
  return { color: "var(--ink-muted)", icon: "—" };
}

// ── PulsingDot ────────────────────────────────────────────────────────────────

export function PulsingDot({ color }: { color: string }) {
  const [on, setOn] = useState(true);
  useEffect(() => {
    const t = setInterval(() => setOn(v => !v), 600);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{
      width: 6, height: 6, borderRadius: "50%",
      background: on ? color : "transparent",
      border: `1.5px solid ${color}`,
      transition: "background 0.3s", flexShrink: 0,
    }} />
  );
}

// ── types ─────────────────────────────────────────────────────────────────────

export interface ExecRow {
  id: string;
  agent_id: string;
  agent_name?: string;
  conversation_id: string | null;
  type: string;
  channel?: string | null;
  status: string;
  started_at: string;
  ended_at: string | null;
  duration_ms: number | null;
  llm_calls: number;
  tokens_total: number;
}

interface ExecutionStep {
  id: string; execution_id: string; order_idx: number;
  type: string; status: string; data: Record<string, unknown>;
  started_at: string; ended_at: string | null; duration_ms: number | null;
}

interface WebhookPayload {
  found: boolean;
  payload: Record<string, unknown> | null;
  received_at: string | null;
  endpoint: string | null;
}

// ── StepBlock (antiga ConvPopup removida — usar ConversationModal) ────────────

// ── StepBlock ─────────────────────────────────────────────────────────────────

// ToolCall, buildCurl e ToolCallPopup importados de ./ToolCallPopup

function StepBlock({
  icon, iconColor, title, summary, duration, isLast, isRunning, data, expandText, customBody, actionButton, dimmed,
}: {
  icon: string; iconColor: string; title: string; summary?: string;
  duration: number | null; isLast: boolean; isRunning?: boolean;
  data?: Record<string, unknown>;
  expandText?: string;
  customBody?: React.ReactNode;
  actionButton?: { label: string; onClick: () => void } | { label: string; onClick: () => void }[];
  dimmed?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const hasData = customBody != null || expandText != null || (data && Object.keys(data).filter(k => k !== "status").length > 0);

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", opacity: dimmed ? 0.35 : 1 }}>
      {/* circle + connector */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <div style={{
          width: 24, height: 24, borderRadius: "50%",
          background: `${iconColor}22`, border: `1.5px solid ${iconColor}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, fontWeight: 700, color: iconColor,
        }}>
          {isRunning ? <PulsingDot color={iconColor} /> : icon}
        </div>
        {!isLast && <div style={{ width: 1, flex: 1, background: "var(--line-strong)", minHeight: 20, margin: "3px 0" }} />}
      </div>

      {/* card */}
      <div style={{
        flex: 1, paddingBottom: !isLast ? 8 : 0,
        background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden",
      }}>
        {/* header */}
        <div
          onClick={() => hasData && setOpen(v => !v)}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 12px", cursor: hasData ? "pointer" : "default",
          }}
          onMouseOver={e => { if (hasData) e.currentTarget.style.background = "var(--line-soft)"; }}
          onMouseOut={e => { e.currentTarget.style.background = ""; }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-mid)", textTransform: "uppercase", letterSpacing: ".04em" }}>
              {title}
            </span>
            {summary && (
              <div style={{ fontSize: 12, color: "var(--ink-strong)", marginTop: 2, lineHeight: 1.4 }}>{summary}</div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 8 }}>
            {actionButton && (Array.isArray(actionButton) ? actionButton : [actionButton]).map((btn, bi) => (
              <button
                key={bi}
                onClick={e => { e.stopPropagation(); btn.onClick(); }}
                style={{
                  fontSize: 11, padding: "2px 8px", borderRadius: 4, cursor: "pointer",
                  border: "1px solid var(--line-strong)", background: "var(--line)", color: "var(--ink-secondary)",
                }}
                onMouseOver={e => { e.currentTarget.style.borderColor = "var(--violet-alt)"; e.currentTarget.style.color = "var(--violet)"; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.color = "var(--ink-secondary)"; }}
              >
                {btn.label}
              </button>
            ))}
            <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>{fmtDuration(duration)}</span>
            {hasData && (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ink-dim)" strokeWidth="2"
                style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </div>
        </div>

        {/* expanded body */}
        {open && hasData && (
          <div style={{ borderTop: "1px solid var(--line)", padding: "10px 12px" }}>
            {customBody != null ? customBody
              : expandText != null ? (
                <p style={{ margin: 0, fontSize: 12, color: "var(--ink-strong)", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                  {expandText}
                </p>
              ) : (
                <pre style={{ margin: 0, fontSize: 11, color: "var(--ink-mid)", whiteSpace: "pre-wrap", wordBreak: "break-all", fontFamily: "var(--font-mono)", lineHeight: 1.6 }}>
                  {JSON.stringify(Object.fromEntries(Object.entries(data!).filter(([k]) => k !== "status")), null, 2)}
                </pre>
              )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ExecModal ─────────────────────────────────────────────────────────────────

interface ExecModalProps {
  exec: ExecRow;
  stepsUrl: string;
  subtitle?: string;
  onClose: () => void;
  /** Aberta de dentro de uma ConversationModal (que já está em modal+100):
   *  sobe mais um nível, senão fica atrás do ExecModal que abriu a conversa. */
  nested?: boolean;
}

export function ExecModal({ exec, stepsUrl, subtitle, onClose, nested }: ExecModalProps) {
  const isAdmin = (_authedRoute.useRouteContext() as { user?: CurrentUser }).user?.role_name === "Admin";
  const isRunning = exec.status === "running";
  const m = execMeta(exec.status);
  const [convPopupId, setConvPopupId] = useState<string | null>(null);
  const [convFullMode, setConvFullMode] = useState(false);
  const [toolPopup, setToolPopup] = useState<ToolCall | null>(null);
  const [webhookCopied, setWebhookCopied] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);
  const [llmRaw, setLlmRaw] = useState<unknown[] | null>(null);
  const [detailed, setDetailed] = useState(false);

  const { data: steps = [], isLoading } = useQuery<ExecutionStep[]>({
    queryKey: ["exec-steps", exec.id, stepsUrl],
    queryFn: () => api.get(stepsUrl),
    refetchInterval: isRunning ? 2000 : false,
  });

  // Nº de mensagens que entraram no contexto do agente (passo load_history),
  // usado para o divisor de contexto no histórico da conversa.
  const histStep = steps.find(s => s.type === "load_history");
  const contextCount = histStep ? Number(histStep.data.messages ?? 0) : 0;

  const { data: webhookData } = useQuery<WebhookPayload>({
    queryKey: ["webhook-payload", exec.id],
    queryFn: () => api.get(`/dashboard/executions/${exec.id}/webhook-payload`),
    enabled: exec.type === "process_message",
  });

  // Carga ORIGINAL da Meta (pré-normalização) — só busca quando a popup abre.
  const { data: rawData } = useQuery<{ found: boolean; payload: unknown }>({
    queryKey: ["webhook-raw", exec.id],
    queryFn: () => api.get(`/dashboard/executions/${exec.id}/webhook-raw`),
    enabled: rawOpen,
  });

  const SIMPLE_STEPS = new Set(["debounce_check", "media_detection", "final_message", "load_history", "llm_tool_loop", "llm", "estoque_tool", "web_tool", "send_message"]);

  // Filtra steps ocultos; no modo simples, mostra apenas os steps essenciais + regras condicionais
  const visibleSteps = steps
    .filter(s => STEP_LABELS[s.type] !== "__hidden__")
    .filter(s => {
      if (detailed) return true;
      if (s.type === "cancelled") return true;                                        // cancelamento sempre visível
      if (s.type === "followup_client") return true;                                  // steps de cliente sempre visíveis
      if (s.status === "error") return true;                                          // erros sempre visíveis
      if (SIMPLE_STEPS.has(s.type)) return true;
      // Tool que chamou ai() interno: mostra no modo normal quando consumiu tokens.
      if (s.type === "subagent_llm" && Number(s.data.tokens_in ?? 0) + Number(s.data.tokens_out ?? 0) > 0) return true;
      if (s.type === "voice" && Number(s.data.tokens_in ?? 0) + Number(s.data.tokens_out ?? 0) > 0) return true;
      if (s.type === "tts" && Number(s.data.tokens_in ?? 0) + Number(s.data.tokens_out ?? 0) > 0) return true;
      if (s.type === "block_check" && (s.status === "blocked" || s.status === "ai_paused")) return true; // humano / pausa
      if (s.type === "ai_pause_set") return true;                                     // tool pausou a IA
      if (s.type === "human_detection" && s.status === "human_block_set") return true;
      if (s.type === "crm_ia_command") return true;                                   // botão do CRM
      if (s.type === "agent_inactive") return true;                                   // agente desativado
      if (s.type === "stale_message") return true;                                    // chegou depois de 15 min
      if (s.type === "interaction_limit_check" && s.status === "blocked_max_messages") return true;
      if (s.type === "ad_detection" && s.data.found) return true;                    // ads detectados
      return false;
    });

  // Modo de envio da resposta (texto/áudio) e mídia — derivado do step de envio,
  // exibido no passo do Agente de IA (onde a resposta é gerada).
  const sendStep = steps.find(s => s.type === "send_message");
  const respMode: "audio" | "text" | null = sendStep
    ? (sendStep.data.mode === "audio" ? "audio" : "text")
    : null;
  const respAudioUrl = sendStep?.data.audio_url as string | undefined;
  const respAudioText = sendStep?.data.audio_text as string | undefined;

  // Follow-up: um card por lead. Os passos do lead (llm do worker leva
  // `telefone`; delivery_failed do hooks leva `recipient`) vão DENTRO do card
  // do followup_client dele; o card fica vermelho se qualquer um falhou.
  // ponytail: casa pelos 8 últimos dígitos (9º dígito / DDI variam entre
  // worker e Meta); dois leads do lote colidindo vão pro primeiro.
  const leadSubs = new Map<string, ExecutionStep[]>();
  let topSteps = visibleSteps;
  if (exec.type === "followup") {
    const clients = visibleSteps.filter(x => x.type === "followup_client");
    const last8 = (v: unknown) => String(v ?? "").replace(/\D/g, "").slice(-8);
    const owned = new Set<string>();
    for (const x of visibleSteps) {
      if (x.type === "followup_client") continue;
      const key = last8(x.data.telefone ?? x.data.recipient);
      const c = key ? clients.find(cl => last8(cl.data.telefone) === key) : undefined;
      if (!c) continue;
      leadSubs.set(c.id, [...(leadSubs.get(c.id) ?? []), x]);
      owned.add(x.id);
    }
    topSteps = visibleSteps.filter(x => !owned.has(x.id));
  }

  // Um passo → card. Recursivo: o card do lead (follow-up) renderiza os
  // passos dele por dentro.
  function renderStep(step: ExecutionStep, isLast: boolean): React.ReactNode {
              if (step.type === "followup_client") {
                const d = step.data as Record<string, any>;
                const outcome = String(d.outcome ?? "");
                const tel = String(d.telefone ?? "—");
                const OUT: Record<string, { label: string; color: string }> = {
                  sent:                { label: "Enviado",                color: "var(--ok)" },
                  sent_template:       { label: "Template enviado",       color: "var(--ok)" },
                  window_closed:       { label: "Janela 24h fechada",     color: "var(--orange)" },
                  template_param_empty: { label: "Template c/ variável vazia", color: "var(--danger)" },
                  template_rejected:   { label: "Template recusado pela Meta", color: "var(--danger)" },
                  connection_down:     { label: "WABA desconectada",      color: "var(--orange)" },
                  closed_history:      { label: "Encerrado",              color: "var(--ink-soft)" },
                  closed_limit:        { label: "Encerrado (limite)",     color: "var(--ink-soft)" },
                  closed_limit_silent: { label: "Encerrado (silencioso)", color: "var(--ink-soft)" },
                  closed_window:       { label: "Encerrado (janela fechada)", color: "var(--ink-soft)" },
                  closed_window_silent: { label: "Encerrado (janela fechada, silencioso)", color: "var(--ink-soft)" },
                  skipped:           { label: "Pulado",                 color: "var(--ink-muted)" },
                  human_blocked:       { label: "Bloqueado (humano)",     color: "var(--orange)" },
                  no_conversation:     { label: "Sem conversa",           color: "var(--ink-muted)" },
                  no_history:          { label: "Sem histórico",          color: "var(--ink-muted)" },
                  send_failed:         { label: "Falha no envio",         color: "var(--danger)" },
                  error:               { label: "Erro",                   color: "var(--danger)" },
                };
                const meta = OUT[outcome] ?? { label: outcome || "—", color: "var(--ink-soft)" };
                const fc = Number(d.follow_count ?? 0);
                const total = Number(d.total ?? 0);
                const convId = d.conversation_id as string | undefined;
                const cardSummary = total > 0 ? `${meta.label} · ${fc}/${total}` : meta.label;
                const tc = d.tool_call as ToolCall | null | undefined;
                const body: React.ReactNode = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {(outcome === "sent" || outcome === "sent_template") && d.message && (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" }}>Mensagem enviada</div>
                        <p style={{ margin: 0, fontSize: 12, color: "var(--ink-strong)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{String(d.message)}</p>
                      </div>
                    )}
                    {(outcome === "closed_history" || outcome === "closed_limit" || outcome === "closed_window") && tc && (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" }}>Tool de encerramento</div>
                        <span onClick={() => setToolPopup(tc)} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, border: "1px solid var(--brand)", color: "var(--brand)", background: "var(--brand-soft)", cursor: "pointer" }}>
                          {tc.name}
                        </span>
                        <pre style={{ margin: "8px 0 0", fontSize: 11, color: "var(--ink-mid)", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono)", lineHeight: 1.5 }}>
                          {JSON.stringify((tc as any).body ?? (tc as any).arguments ?? {}, null, 2)}
                        </pre>
                      </div>
                    )}
                    {(outcome === "window_closed" || outcome === "sent_template" || outcome.startsWith("closed_window") || (outcome === "send_failed" && d.window)) && (
                      <span style={{ fontSize: 12, color: "var(--ink-mid)" }}>
                        {d.window === "unknown" ? `Consulta da janela falhou${d.error && outcome === "window_closed" ? ` (${d.error})` : ""}`
                          : d.window === "open" ? "Janela 24h aberta" : "Janela 24h fechada"}
                        {d.reason === "send_131047" ? " · Meta recusou o envio (131047)" : ""}
                        {" · "}{d.template ? `template ${d.template}` : "sem template cadastrado"}
                        {d.last_inbound_at ? ` · último inbound ${new Date(d.last_inbound_at + "Z").toLocaleString("pt-BR")}` : ""}
                      </span>
                    )}
                    {(outcome === "closed_limit_silent" || outcome === "closed_window_silent") && (
                      <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Encerrado sem tool configurada.</span>
                    )}
                    {(outcome === "send_failed" || outcome === "error") && d.error && (
                      <CauseBox d={d} />
                    )}
                  </div>
                );
                const subs = leadSubs.get(step.id) ?? [];
                const subErr = subs.some(x => x.type === "delivery_failed" || x.status === "error" || x.data.status === "error");
                const failed = subErr || outcome === "send_failed" || outcome === "error";
                const bodyWithSubs = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {body}
                    {subs.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {subs.map((x, j) => renderStep(x, j === subs.length - 1))}
                      </div>
                    )}
                  </div>
                );
                return (
                  <StepBlock
                    key={step.id}
                    icon={outcome === "sent" || outcome === "sent_template" ? (failed ? "✕" : "✓") : failed ? "✕" : "—"}
                    iconColor={failed ? "var(--danger)" : meta.color}
                    title={tel}
                    summary={subs.some(x => x.type === "delivery_failed") ? `${cardSummary} · entrega recusada pela Meta` : cardSummary}
                    duration={step.duration_ms}
                    isLast={isLast}
                    customBody={bodyWithSubs}
                    actionButton={convId ? { label: "Ver conversa", onClick: () => { setConvFullMode(true); setConvPopupId(convId); } } : undefined}
                  />
                );
              }
              const sv = stepVis(step.status);
              const MEDIA_TYPE_LABELS: Record<string, string> = {
                audioMessage: "Áudio", videoMessage: "Vídeo", imageMessage: "Imagem",
                stickerMessage: "Figurinha", documentMessage: "Documento",
                audio: "Áudio", video: "Vídeo", image: "Imagem",
                sticker: "Figurinha", document: "Documento",
              };
              const isTranscription = step.type === "transcription" || step.type === "pre_transcription";
              const label = isTranscription
                ? (() => {
                    const mt = step.data.media_type as string | undefined;
                    const typeName = mt ? (MEDIA_TYPE_LABELS[mt] ?? mt) : "";
                    return typeName ? `Transcrição de Mídia — ${typeName}` : "Transcrição de Mídia";
                  })()
                : (STEP_LABELS[step.type] ?? step.type);
              // No passo do Agente de IA, sinaliza no título se a resposta saiu em Texto ou Áudio.
              const isLlmStep = step.type === "llm_tool_loop" || step.type === "llm";
              const labelWithMode = isLlmStep && respMode
                ? `${label} — ${respMode === "audio" ? "Áudio" : "Texto"}`
                : label;
              const summary = stepSummary(step.type, step.data);
              const isHistoryStep = step.type === "load_history" && exec.conversation_id;
              const isDimmed = step.type === "crmillion" && step.data.status === "disabled";
              const expandData = (NO_EXPAND_STEPS.has(step.type) || isTranscription) ? undefined : step.data;

              // Texto puro para mensagem final e detecção de ads (transcrição usa customBody)
              const expandText =
                step.type === "final_message"
                  ? (step.data.content as string | undefined)
                  : step.type === "ad_detection" && step.data.found
                  ? (step.data.summary as string | undefined) ?? ""
                  : undefined;

              // Body customizado para transcrição, load_tools, llm_tool_loop e send_message
              let customBody: React.ReactNode = undefined;

              // Erro: qualquer passo que falhou mostra a mensagem + traceback completo.
              const stepStatus = (step.data.status as string | undefined) ?? "ok";
              const errorBody = (stepStatus === "error" || stepStatus === "warn") && (step.data.error || step.data.traceback) ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 10, color: "var(--danger)", textTransform: "uppercase", letterSpacing: ".04em" }}>Erro</div>
                  {step.data.error != null && <CauseBox d={step.data as Record<string, any>} />}
                  {step.data.traceback != null && isAdmin && (
                    <pre style={{ margin: 0, fontSize: 11, color: "var(--ink-mid)", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono)", lineHeight: 1.5, maxHeight: 320, overflowY: "auto", background: "var(--surface-raised)", borderRadius: 6, padding: 8 }}>{String(step.data.traceback)}</pre>
                  )}
                </div>
              ) : null;

              if (isTranscription) {
                const output = step.data.output as string | undefined;
                customBody = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <SubagentMeta data={step.data} />
                    {output && (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>Resposta do agente de transcrição</div>
                        <p style={{ margin: 0, fontSize: 12, color: "var(--ink-strong)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{output}</p>
                      </div>
                    )}
                    {errorBody}
                  </div>
                );
              }

              if (step.type === "ad_detection" && step.data.found) {
                const summary = step.data.summary as string | undefined;
                customBody = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <SubagentMeta data={step.data} />
                    {summary && (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>Resumo do anúncio</div>
                        <p style={{ margin: 0, fontSize: 12, color: "var(--ink-strong)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{summary}</p>
                      </div>
                    )}
                    {errorBody}
                  </div>
                );
              }

              if (step.type === "voice") {
                const adapted = step.data.output as string | undefined;
                customBody = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <SubagentMeta data={step.data} />
                    {adapted && (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>Texto adaptado para fala</div>
                        <p style={{ margin: 0, fontSize: 12, color: "var(--ink-strong)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{adapted}</p>
                      </div>
                    )}
                    {errorBody}
                  </div>
                );
              }

              if (step.type === "tts") {
                customBody = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <SubagentMeta data={step.data} />
                    {errorBody}
                  </div>
                );
              }

              if (step.type === "load_tools") {
                const names = (step.data.names as string[] | undefined) ?? [];
                customBody = names.length > 0 ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {names.map(n => (
                      <span key={n} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, border: "1px solid var(--brand)", color: "var(--brand)", background: "var(--brand-soft)" }}>
                        {n}
                      </span>
                    ))}
                  </div>
                ) : <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>Nenhuma ferramenta configurada</span>;
              }

              if (step.type === "prompt_cache") {
                const used = Boolean(step.data.used_cache);
                const expires = step.data.expires_at as string | undefined;
                const chars = Number(step.data.prompt_chars ?? 0);
                const warn = step.data.warning_vars as string | undefined;
                const cached = step.data.prompt as string | undefined;
                const OUTCOME: Record<string, string> = {
                  hit: "Reaproveitado", created: "Criado agora",
                  // Anthropic: leitura/escrita só se sabe na chamada — ver Cache/Escrita no step do agente.
                  anthropic: "Ligado — leitura/escrita na chamada do agente",
                };
                const outcome = OUTCOME[String(step.data.cache_outcome ?? "")] ?? "Não utilizado";
                customBody = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ fontSize: 11, color: "var(--ink-muted)", display: "flex", flexWrap: "wrap", gap: 8 }}>
                      <span>Situação: <strong style={{ color: used ? "var(--brand)" : "var(--ink-mid)" }}>{outcome}</strong></span>
                      {chars > 0 && <span>Prompt cacheado: <strong style={{ color: "var(--ink-mid)" }}>{chars.toLocaleString()} chars</strong></span>}
                      {expires && <span>Válido até: <strong style={{ color: "var(--ink-mid)" }}>{new Date(expires).toLocaleTimeString("pt-BR")}</strong></span>}
                      {!used && <span>Cobrança: <strong style={{ color: "var(--ink-mid)" }}>prompt integral</strong></span>}
                    </div>
                    {warn && (
                      <div style={{ fontSize: 11, color: "var(--warn)", background: "var(--warn-soft)", borderRadius: 6, padding: 8 }}>
                        {warn}
                      </div>
                    )}
                    {step.data.cache_name != null && (
                      <div style={{ fontSize: 10, color: "var(--ink-dim)", fontFamily: "var(--font-mono)", wordBreak: "break-all" }}>
                        {String(step.data.cache_name)}
                      </div>
                    )}
                    {cached && (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" }}>
                          Prompt no cache (sem renderizar variáveis)
                        </div>
                        <pre style={{ margin: 0, fontSize: 11, color: "var(--ink-strong)", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono)", lineHeight: 1.5, background: "var(--surface-raised)", borderRadius: 6, padding: 8, maxHeight: 280, overflowY: "auto" }}>{cached}</pre>
                      </div>
                    )}
                    {errorBody}
                  </div>
                );
              }

              if (step.type === "llm_tool_loop" || step.type === "llm") {
                const tcs = (step.data.tool_calls as ToolCall[] | undefined) ?? [];
                const sysMsg = step.data.system_message as string | undefined;
                const resp = step.data.response as string | undefined;
                const promptSize = Number(step.data.prompt_size ?? 0);
                const iters = Number(step.data.steps ?? 1);
                const model = step.data.model as string | undefined;
                const temp = step.data.temperature as number | undefined;
                const tin = Number(step.data.tokens_in ?? 0);
                const tout = Number(step.data.tokens_out ?? 0);
                const tthink = Number(step.data.tokens_thinking ?? 0);
                const tcache = Math.min(Number(step.data.tokens_cached ?? 0), tin);
                const twrite = Number(step.data.tokens_cache_write ?? 0);
                const llmCost = stepCost(step.data);
                customBody = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ fontSize: 11, color: "var(--ink-muted)", display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {model && <span>Modelo: <strong style={{ color: "var(--ink-mid)" }}>{model}</strong></span>}
                      {temp != null && <span>Temperatura: <strong style={{ color: "var(--ink-mid)" }}>{temp}</strong></span>}
                      <span>Prompt: <strong style={{ color: "var(--ink-mid)" }}>{promptSize.toLocaleString()} chars</strong></span>
                      <span>Iterações: <strong style={{ color: "var(--ink-mid)" }}>{iters}</strong></span>
                      {tin > 0 && <span>Input: <strong style={{ color: "var(--ink-mid)" }}>{(tin - tcache - twrite).toLocaleString()} tk</strong></span>}
                      {/* Cache sempre visível (mesmo 0) — mostra de cara se a chamada pegou desconto. */}
                      <span>Cache: <strong style={{ color: "var(--ink-mid)" }}>{tcache.toLocaleString()} tk</strong></span>
                      {twrite > 0 && <span>Escrita cache: <strong style={{ color: "var(--ink-mid)" }}>{twrite.toLocaleString()} tk</strong></span>}
                      {tout > 0 && <span>Output: <strong style={{ color: "var(--ink-mid)" }}>{tout.toLocaleString()} tk</strong></span>}
                      {/* Anthropic não separa o thinking: ele vem somado no output. */}
                      <span>Thinking: <strong style={{ color: "var(--ink-mid)" }}>{step.data.model_provider === "anthropic" ? "incluso no output" : `${tthink.toLocaleString()} tk`}</strong></span>
                      {llmCost > 0 && <span>Custo: <strong style={{ color: "var(--ink-mid)" }}>${fmtUsd(llmCost)}</strong></span>}
                      {Array.isArray(step.data.llm_raw) && step.data.llm_raw.length > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setLlmRaw(step.data.llm_raw as unknown[]); }}
                          style={{
                            fontSize: 11, padding: "1px 8px", borderRadius: 4, cursor: "pointer",
                            border: "1px solid var(--line-strong)", background: "var(--line)", color: "var(--ink-secondary)",
                          }}
                        >
                          Ver retorno da LLM
                        </button>
                      )}
                    </div>
                    {sysMsg && (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" }}>
                          Mensagem do sistema (injetada nesta chamada)
                        </div>
                        <p style={{ margin: 0, fontSize: 12, color: "var(--ink-secondary)", whiteSpace: "pre-wrap", lineHeight: 1.5, maxHeight: 160, overflowY: "auto" }}>{sysMsg}</p>
                      </div>
                    )}
                    {tcs.length > 0 && (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" }}>Tools utilizadas</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {tcs.map((tc, idx) => (
                            <span key={idx} onClick={() => setToolPopup(tc)} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, border: "1px solid var(--brand)", color: "var(--brand)", background: "var(--brand-soft)", cursor: "pointer" }}
                              onMouseOver={e => (e.currentTarget.style.background = "rgba(195,32,32,0.18)")}
                              onMouseOut={e => (e.currentTarget.style.background = "var(--brand-soft)")}
                            >
                              {tc.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {resp && (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em", display: "flex", alignItems: "center", gap: 8 }}>
                          <span>Resposta do agente</span>
                          {respMode && (
                            <span style={{
                              fontSize: 9, padding: "1px 6px", borderRadius: 99,
                              background: respMode === "audio" ? "rgba(195,32,32,0.12)" : "var(--surface-hover)",
                              color: respMode === "audio" ? "var(--brand)" : "var(--ink-mid)",
                            }}>
                              {respMode === "audio" ? <><MicIcon size={12} /> Áudio</> : "Texto"}
                            </span>
                          )}
                        </div>
                        {/* Destaque na resposta: é o que interessa ao bater o olho. */}
                        <p style={{ margin: 0, fontSize: 12, color: "var(--ink-strong)", whiteSpace: "pre-wrap", lineHeight: 1.5, background: "var(--surface-raised)", borderRadius: 6, padding: 8 }}>{resp}</p>
                      </div>
                    )}
                    {respMode === "audio" && respAudioText && (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" }}>Texto falado (adaptado para o áudio)</div>
                        <p style={{ margin: 0, fontSize: 12, color: "var(--ink-strong)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{respAudioText}</p>
                      </div>
                    )}
                    {errorBody}
                  </div>
                );
              }

              if (step.type === "subagent_llm") {
                const toolName = String(step.data.agent_label ?? "tool").replace(/^tool:/, "");
                const model = step.data.model_name as string | undefined;
                const provider = step.data.model_provider as string | undefined;
                const tin = Number(step.data.tokens_in ?? 0);
                const tout = Number(step.data.tokens_out ?? 0);
                const tthink = Number(step.data.tokens_thinking ?? 0);
                const tcache = Math.min(Number(step.data.tokens_cached ?? 0), tin);
                const cost = stepCost(step.data);
                const args = step.data.arguments as Record<string, unknown> | undefined;
                const resultText = step.data.result as string | undefined;
                const sectionLabel: React.CSSProperties = { fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" };
                const preBox: React.CSSProperties = { margin: 0, fontSize: 11, color: "var(--ink-strong)", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono)", lineHeight: 1.5, background: "var(--surface-raised)", borderRadius: 6, padding: 8, maxHeight: 280, overflowY: "auto" };
                customBody = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ fontSize: 11, color: "var(--ink-muted)", display: "flex", flexWrap: "wrap", gap: 8 }}>
                      <span>Ferramenta: <strong style={{ color: "var(--brand)" }}>{toolName}</strong></span>
                      {model && <span>Modelo: <strong style={{ color: "var(--ink-mid)" }}>{provider ? `${provider}/` : ""}{model}</strong></span>}
                      {tin > 0 && <span>Input: <strong style={{ color: "var(--ink-mid)" }}>{(tin - tcache).toLocaleString()} tk</strong></span>}
                      {tcache > 0 && <span>Cache: <strong style={{ color: "var(--ink-mid)" }}>{tcache.toLocaleString()} tk</strong></span>}
                      {tout > 0 && <span>Output: <strong style={{ color: "var(--ink-mid)" }}>{tout.toLocaleString()} tk</strong></span>}
                      {tthink > 0 && <span>Thinking: <strong style={{ color: "var(--ink-mid)" }}>{tthink.toLocaleString()} tk</strong></span>}
                      {cost > 0 && <span>Custo: <strong style={{ color: "var(--ink-mid)" }}>${fmtUsd(cost)}</strong></span>}
                    </div>
                    {args && Object.keys(args).length > 0 && (
                      <div>
                        <div style={sectionLabel}>Parâmetros da ferramenta</div>
                        <pre style={preBox}>{JSON.stringify(args, null, 2)}</pre>
                      </div>
                    )}
                    {resultText && (
                      <div>
                        <div style={sectionLabel}>Resposta da ferramenta</div>
                        <pre style={preBox}>{resultText}</pre>
                      </div>
                    )}
                    {errorBody}
                  </div>
                );
              }

              if (step.type === "web_tool") {
                const model = step.data.model_name as string | undefined;
                const provider = step.data.model_provider as string | undefined;
                const tin = Number(step.data.tokens_in ?? 0);
                const tout = Number(step.data.tokens_out ?? 0);
                const cost = stepCost(step.data);
                const OUTCOME: Record<string, string> = {
                  hit: "Reaproveitado (cache)", pinned: "Reaproveitado (cache fixo)", miss: "Busca nova",
                  expired: "Cache expirado, busca nova", fetched: "Busca nova + páginas abertas",
                };
                const outcome = OUTCOME[String(step.data.cache_outcome ?? "")] ?? "Busca";
                const sources = Array.isArray(step.data.sources) ? (step.data.sources as string[]) : [];
                const unresp = Array.isArray(step.data.unresponsive_engines) ? (step.data.unresponsive_engines as string[]) : [];
                const resultText = step.data.result as string | undefined;
                const sectionLabel: React.CSSProperties = { fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" };
                const preBox: React.CSSProperties = { margin: 0, fontSize: 11, color: "var(--ink-strong)", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono)", lineHeight: 1.5, background: "var(--surface-raised)", borderRadius: 6, padding: 8, maxHeight: 280, overflowY: "auto" };
                customBody = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ fontSize: 11, color: "var(--ink-muted)", display: "flex", flexWrap: "wrap", gap: 8 }}>
                      <span>Ferramenta: <strong style={{ color: "var(--brand)" }}>buscar_na_internet</strong></span>
                      {step.data.query != null && <span>Busca: <strong style={{ color: "var(--ink-mid)" }}>{String(step.data.query)}</strong></span>}
                      <span>Cache: <strong style={{ color: "var(--ink-mid)" }}>{outcome}</strong></span>
                      {step.data.results != null && <span>Resultados: <strong style={{ color: "var(--ink-mid)" }}>{String(step.data.results)}</strong></span>}
                      {Number(step.data.pages ?? 0) > 0 && <span>Páginas abertas: <strong style={{ color: "var(--ink-mid)" }}>{String(step.data.pages)}</strong></span>}
                      {step.data.elapsed_ms != null && <span>Tempo: <strong style={{ color: "var(--ink-mid)" }}>{(Number(step.data.elapsed_ms) / 1000).toFixed(1)}s</strong></span>}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ink-muted)", display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {model && <span>Modelo: <strong style={{ color: "var(--ink-mid)" }}>{provider ? `${provider}/` : ""}{model}</strong></span>}
                      {tin > 0 && <span>Input: <strong style={{ color: "var(--ink-mid)" }}>{tin.toLocaleString()} tk</strong></span>}
                      {tout > 0 && <span>Output: <strong style={{ color: "var(--ink-mid)" }}>{tout.toLocaleString()} tk</strong></span>}
                      {cost > 0 && <span>Custo: <strong style={{ color: "var(--ink-mid)" }}>${fmtUsd(cost)}</strong></span>}
                      {unresp.length > 0 && <span>Motores sem resposta: <strong style={{ color: "var(--ink-mid)" }}>{unresp.join(", ")}</strong></span>}
                      {step.data.library_error != null && <span>Biblioteca: <strong style={{ color: "var(--ink-mid)" }}>erro ({String(step.data.library_error)})</strong></span>}
                    </div>
                    {sources.length > 0 && (
                      <div>
                        <div style={sectionLabel}>Fontes</div>
                        <div style={{ fontSize: 11, display: "flex", flexDirection: "column", gap: 2 }}>
                          {sources.map((u) => <a key={u} href={u} target="_blank" rel="noreferrer" style={{ color: "var(--brand)", wordBreak: "break-all" }}>{u}</a>)}
                        </div>
                      </div>
                    )}
                    {resultText && (
                      <div>
                        <div style={sectionLabel}>Resposta da ferramenta</div>
                        <pre style={preBox}>{resultText}</pre>
                      </div>
                    )}
                    {errorBody}
                  </div>
                );
              }

              if (step.type === "estoque_tool") {
                const toolName = String(step.data.agent_label ?? "tool").replace(/^tool:/, "");
                const model = step.data.model_name as string | undefined;
                const provider = step.data.model_provider as string | undefined;
                const tin = Number(step.data.tokens_in ?? 0);
                const tout = Number(step.data.tokens_out ?? 0);
                const tcache = Math.min(Number(step.data.tokens_cached ?? 0), tin);
                const cost = stepCost(step.data);
                const OUTCOME: Record<string, string> = {
                  hit: "Reaproveitado", created: "Criado agora", fallback: "Falhou no uso (rodou integral)",
                };
                const outcome = OUTCOME[String(step.data.cache_outcome ?? "")] ?? "Não utilizado";
                const cacheReason = step.data.cache_reason as string | undefined;
                const args = step.data.arguments as Record<string, unknown> | undefined;
                const resultText = step.data.result as string | undefined;
                const sectionLabel: React.CSSProperties = { fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" };
                const preBox: React.CSSProperties = { margin: 0, fontSize: 11, color: "var(--ink-strong)", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono)", lineHeight: 1.5, background: "var(--surface-raised)", borderRadius: 6, padding: 8, maxHeight: 280, overflowY: "auto" };
                customBody = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ fontSize: 11, color: "var(--ink-muted)", display: "flex", flexWrap: "wrap", gap: 8 }}>
                      <span>Ferramenta: <strong style={{ color: "var(--brand)" }}>{toolName}</strong></span>
                      {step.data.loja != null && <span>Loja: <strong style={{ color: "var(--ink-mid)" }}>{String(step.data.loja)}</strong></span>}
                      {step.data.rota != null && <span>Rota: <strong style={{ color: "var(--ink-mid)" }}>{String(step.data.rota)}</strong></span>}
                      {model && <span>Modelo: <strong style={{ color: "var(--ink-mid)" }}>{provider ? `${provider}/` : ""}{model}</strong></span>}
                      {tin > 0 && <span>Input: <strong style={{ color: "var(--ink-mid)" }}>{(tin - tcache).toLocaleString()} tk</strong></span>}
                      {tcache > 0 && <span>Cache: <strong style={{ color: "var(--ink-mid)" }}>{tcache.toLocaleString()} tk</strong></span>}
                      {tout > 0 && <span>Output: <strong style={{ color: "var(--ink-mid)" }}>{tout.toLocaleString()} tk</strong></span>}
                      {Number(step.data.ai_calls ?? 0) > 0 && <span>Chamadas de IA: <strong style={{ color: "var(--ink-mid)" }}>{String(step.data.ai_calls)}</strong></span>}
                      {cost > 0 && <span>Custo: <strong style={{ color: "var(--ink-mid)" }}>${fmtUsd(cost)}</strong></span>}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ink-muted)", display: "flex", flexWrap: "wrap", gap: 8 }}>
                      <span>Cache de estoque: <strong style={{ color: "var(--ink-mid)" }}>{outcome}</strong></span>
                      {cacheReason && <span>Motivo: <strong style={{ color: "var(--ink-mid)" }}>{cacheReason}</strong></span>}
                      {step.data.cache_expires_at != null && <span>Válido até: <strong style={{ color: "var(--ink-mid)" }}>{new Date(String(step.data.cache_expires_at)).toLocaleTimeString("pt-BR")}</strong></span>}
                      {step.data.list_chars != null && <span>Estoque: <strong style={{ color: "var(--ink-mid)" }}>{Number(step.data.list_chars).toLocaleString()} chars</strong></span>}
                    </div>
                    {args && Object.keys(args).length > 0 && (
                      <div>
                        <div style={sectionLabel}>Parâmetros da ferramenta</div>
                        <pre style={preBox}>{JSON.stringify(args, null, 2)}</pre>
                      </div>
                    )}
                    {resultText && (
                      <div>
                        <div style={sectionLabel}>Resposta da ferramenta</div>
                        <pre style={preBox}>{resultText}</pre>
                      </div>
                    )}
                    {errorBody}
                  </div>
                );
              }

              if (step.type === "send_message" && step.data.response) {
                customBody = (
                  <pre style={{ margin: 0, fontSize: 11, color: "var(--ink-mid)", whiteSpace: "pre-wrap", fontFamily: "var(--font-mono)", lineHeight: 1.6 }}>
                    {JSON.stringify(step.data.response, null, 2)}
                  </pre>
                );
              }

              if (step.type === "crmillion" && (step.data.api != null || step.data.target_type || step.data.existing_attendant)) {
                const tt = step.data.target_type as string | undefined;
                const tname = (step.data.target_name as string) || (step.data.target_id as string) || "";
                const att = step.data.existing_attendant as { name?: string; id?: string } | undefined;
                const sectionLabel: React.CSSProperties = { fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" };
                const preBox: React.CSSProperties = { margin: 0, fontSize: 11, color: "var(--ink-strong)", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono)", lineHeight: 1.5, background: "var(--surface-raised)", borderRadius: 6, padding: 8, maxHeight: 280, overflowY: "auto" };
                customBody = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ fontSize: 11, color: "var(--ink-muted)", display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {tt && <span>Alvo: <strong style={{ color: "var(--ink-mid)" }}>{tt === "team" ? "Equipe" : "Pessoa"}{tname ? ` · ${tname}` : ""}</strong></span>}
                      {att?.name && <span>Atendente existente: <strong style={{ color: "var(--ink-mid)" }}>{att.name}</strong></span>}
                      {step.data.conversation_id != null && <span>Conversa: <strong style={{ color: "var(--ink-mid)" }}>{String(step.data.conversation_id)}</strong></span>}
                    </div>
                    {step.data.api != null && (
                      <div>
                        <div style={sectionLabel}>Resposta da API</div>
                        <pre style={preBox}>{JSON.stringify(step.data.api, null, 2)}</pre>
                      </div>
                    )}
                    {errorBody}
                  </div>
                );
              }

              if (step.type === "render_prompt") {
                const rendered = step.data.prompt as string | undefined;
                const err = step.data.error as string | undefined;
                customBody = (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {err && <div style={{ fontSize: 11, color: "var(--danger)" }}><AlertTriangleIcon size={11} /> {err}</div>}
                    {rendered != null ? (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" }}>
                          Prompt enviado ao agente (variáveis resolvidas)
                        </div>
                        <pre style={{ margin: 0, fontSize: 12, color: "var(--ink-strong)", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono)", lineHeight: 1.5 }}>
                          {rendered}
                        </pre>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>Prompt renderizado não registrado nesta execução.</span>
                    )}
                  </div>
                );
              }

              // Fallback: passo que falhou e não tem corpo próprio → mostra o erro.
              if (customBody == null && errorBody) customBody = errorBody;

              return (
                <StepBlock
                  key={step.id}
                  icon={sv.icon}
                  iconColor={sv.color}
                  title={labelWithMode}
                  summary={summary || undefined}
                  duration={step.duration_ms}
                  isLast={isLast}
                  dimmed={isDimmed}
                  data={customBody != null || expandText != null ? undefined : expandData}
                  expandText={expandText}
                  customBody={customBody}
                  actionButton={
                    isHistoryStep ? {
                      label: "Ver histórico",
                      onClick: () => { setConvFullMode(false); setConvPopupId(exec.conversation_id!); },
                    } : (isLlmStep && respMode === "audio" && respAudioUrl) ? {
                      label: "▶ Ouvir áudio",
                      onClick: () => window.open(respAudioUrl, "_blank"),
                    } : undefined
                  }
                />
              );
  }

  return (
    <>
      {/* Popup de tool call */}
      {toolPopup && <ToolCallPopup tc={toolPopup} onClose={() => setToolPopup(null)} />}

      {/* Popup da carga original da Meta (pré-normalização) */}
      {rawOpen && (
        <JsonPopup
          title="Carga original (Meta)"
          data={rawData == null ? undefined : rawData.found ? orderWire(rawData.payload, "meta") : null}
          emptyText="Carga original não encontrada (mensagem antiga ou canal não-WABA)."
          onClose={() => setRawOpen(false)}
          nested={nested}
        />
      )}

      {/* Popup do retorno cru da LLM (uma entrada por volta do loop de tools) */}
      {llmRaw && (
        <JsonPopup
          title="Retorno da LLM"
          data={llmRaw}
          emptyText=""
          onClose={() => setLlmRaw(null)}
          nested={nested}
        />
      )}

      {/* Popup de conversa */}
      {/* Wrapper com z próprio: a ConversationModal fixa +100 (e o ToolCallPopup
          dela +200), abaixo desta ExecModal quando nested (+200). O stacking
          context sobe tudo junto sem mexer na ordem interna. */}
      {convPopupId && (
        <div style={{ position: "relative", zIndex: "calc(var(--z-modal) + 400)" as any }}>
        <ConversationModal
          agentId={exec.agent_id}
          convId={convPopupId}
          onClose={() => setConvPopupId(null)}
          execContext={convFullMode ? undefined : { contextCount }}
          messagesUrl={
            exec.type === "playground"
              ? `/agents/${exec.agent_id}/playground/conversations/${convPopupId}/messages`
              : undefined
          }
        />
        </div>
      )}

      <div
        {...backdropClose(() => { onClose(); })}
        style={{
          position: "fixed", inset: 0, background: "var(--overlay)",
          zIndex: (nested ? "calc(var(--z-modal) + 200)" : "var(--z-modal)") as any, display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <DialogPanel onClose={() => onClose()} label={`Execução ${exec.id.slice(0, 8)}`} style={{
          background: "var(--surface-hover)", border: "1px solid var(--line-strong)",
          borderRadius: 12, width: 580, maxHeight: "85vh",
          display: "flex", flexDirection: "column",
          boxShadow: "var(--shadow-modal)",
          animation: "execModalIn 0.16s ease",
        }}>
          {/* header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "16px 18px", borderBottom: "1px solid var(--line)", flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Execução {exec.id.slice(0, 8)}</span>
                {subtitle && <div style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 2 }}>{subtitle}</div>}
              </div>
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: m.bg, color: m.color, fontWeight: 500 }}>
                {m.label}
              </span>
            </div>
            {/* Toggle simples / detalhado */}
            <button
              onClick={() => setDetailed(v => !v)}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                fontSize: 12, color: detailed ? "var(--ink)" : "var(--ink-muted)",
                padding: "5px 10px", borderRadius: 8, cursor: "pointer",
                border: `1px solid ${detailed ? "var(--brand)" : "var(--line-strong)"}`,
                background: detailed ? "var(--brand-soft)" : "transparent",
                transition: "all 0.15s",
              }}
            >
              <div style={{
                width: 28, height: 16, borderRadius: 99, position: "relative",
                background: detailed ? "var(--brand)" : "var(--line-strong)",
                transition: "background 0.15s", flexShrink: 0,
              }}>
                <div style={{
                  position: "absolute", top: 2,
                  left: detailed ? 14 : 2,
                  width: 12, height: 12, borderRadius: "50%",
                  background: "#fff", transition: "left 0.15s",
                }} />
              </div>
              Detalhado
            </button>

            <button onClick={onClose}
              style={{ color: "var(--ink-muted)", padding: 4, borderRadius: 4, display: "flex", background: "none", border: "none", cursor: "pointer" }}
              onMouseOver={e => (e.currentTarget.style.color = "var(--ink)")}
              onMouseOut={e => (e.currentTarget.style.color = "var(--ink-muted)")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* body */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
            {/* meta */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20, fontSize: 12, color: "var(--ink-muted)" }}>
              <span>Iniciado: <strong style={{ color: "var(--ink-secondary)" }}>{fmtDate(exec.started_at)}</strong></span>
              <span>Duração: <strong style={{ color: "var(--ink-secondary)" }}>{fmtDuration(exec.duration_ms)}</strong></span>
              <span>Tipo: <strong style={{ color: "var(--ink-secondary)" }}>{typeLabel(exec.type)}</strong></span>
              {exec.llm_calls > 0 && <span>LLM: <strong style={{ color: "var(--ink-secondary)" }}>{exec.llm_calls}x</strong></span>}
              {exec.tokens_total > 0 && <span>Tokens: <strong style={{ color: "var(--ink-secondary)" }}>{exec.tokens_total.toLocaleString()}</strong></span>}
            </div>

            <hr style={{ border: "none", borderTop: "1px solid var(--line)" }} />

            {isLoading && <div style={{ color: "var(--ink-dim)", fontSize: 13 }}>Carregando steps...</div>}

            {visibleSteps.length === 0 && !isLoading && !webhookData?.found && (
              <div style={{ color: "var(--ink-dim)", fontSize: 13 }}>
                {isRunning ? "Aguardando primeiro step..." : "Nenhum step registrado."}
              </div>
            )}

            {/* Webhook inicial */}
            {webhookData?.found && (
              <StepBlock
                icon="↓"
                iconColor="var(--violet-alt)"
                title="Webhook Recebido"
                summary={webhookData.endpoint ? `Endpoint: ${webhookData.endpoint}` : undefined}
                duration={null}
                isLast={visibleSteps.length === 0 && !isRunning}
                data={webhookData.payload ? orderWire(webhookData.payload) as Record<string, unknown> : undefined}
                actionButton={[
                  {
                    label: webhookCopied ? "✓ Copiado" : "Copiar",
                    onClick: () => {
                      navigator.clipboard.writeText(JSON.stringify(orderWire(webhookData.payload), null, 2));
                      setWebhookCopied(true);
                      setTimeout(() => setWebhookCopied(false), 2000);
                    },
                  },
                  { label: "Original (Meta)", onClick: () => setRawOpen(true) },
                ]}
              />
            )}

            {/* Steps */}
            {topSteps.map((step, i) => renderStep(step, i === topSteps.length - 1 && !isRunning))}

            {isRunning && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                <PulsingDot color="var(--info)" />
                <span style={{ fontSize: 12, color: "var(--info)" }}>processando...</span>
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </>
  );
}
